import React, { useState, useEffect } from 'react';
import { AIIdeaGeneratorService } from '../../services/aiIdeaGeneratorService';
import { AIIdeaInput, BusinessIdea, AIServiceError } from '../../types/ai';
import { SkillSelector } from './SkillSelector';
import { IndustrySelector } from './IndustrySelector';
import { ProblemTypeSelector } from './ProblemTypeSelector';
import { MarketSelector } from '../common/MarketSelector';
import { DetailLevelSelector, DetailLevel } from '../common/DetailLevelSelector';
import { LongRunningLoadingState } from '../common/LongRunningLoadingState';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import './QuickFireIdeaGenerator.css';

export interface QuickFireIdeaGeneratorProps {
  onIdeasGenerated: (ideas: BusinessIdea[]) => void;
  onError?: (error: string) => void;
  currentStep?: string;
  showStepsIndicator?: boolean;
}

type FormTab = 'skills' | 'interests' | 'problems';

export const QuickFireIdeaGenerator: React.FC<QuickFireIdeaGeneratorProps> = ({
  onIdeasGenerated,
  onError,
  currentStep,
  showStepsIndicator = false
}) => {
  const { t, uiLanguage } = useLanguage();
  const { user } = useAuth();
  const [answers, setAnswers] = useState<AIIdeaInput>({
    skills: [],
    interests: [],
    problemType: ''
  });
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<FormTab>('skills');
  const [targetMarket, setTargetMarket] = useState('NZ'); // Default to New Zealand
  const [detailLevel, setDetailLevel] = useState<DetailLevel>('standard'); // Detail level for output
  
  // Get user's country from localStorage or context (you can adjust this based on your app's country detection)
  useEffect(() => {
    // Try to get user's country from localStorage or other source
    const userCountry = localStorage.getItem('userCountry') || 'NZ';
    setTargetMarket(userCountry);
  }, []);

  const tabs = [
    { id: 'skills' as FormTab, title: t('aiIdeaGenerator.yourSkills'), icon: '🛠️', subtitle: t('aiIdeaGenerator.whatAreYouGoodAt') },
    { id: 'interests' as FormTab, title: t('aiIdeaGenerator.yourInterests'), icon: '🌟', subtitle: t('aiIdeaGenerator.whichIndustriesExcite') },
    { id: 'problems' as FormTab, title: t('aiIdeaGenerator.problemArea'), icon: '🎯', subtitle: t('aiIdeaGenerator.whatProblemsToSolve') }
  ];

  const isFormValid = (): boolean => {
    return answers.skills.length > 0 && 
           answers.interests.length > 0 && 
           answers.problemType.trim() !== '';
  };

  const getTabStatus = (tabId: FormTab): 'completed' | 'active' | 'pending' => {
    const tabIndex = tabs.findIndex(tab => tab.id === tabId);
    const activeIndex = tabs.findIndex(tab => tab.id === activeTab);
    
    if (tabIndex < activeIndex) return 'completed';
    if (tabIndex === activeIndex) return 'active';
    return 'pending';
  };

  const canNavigateToTab = (tabId: FormTab): boolean => {
    const tabIndex = tabs.findIndex(tab => tab.id === tabId);
    const activeIndex = tabs.findIndex(tab => tab.id === activeTab);
    
    // Can always go back to previous tabs
    if (tabIndex <= activeIndex) return true;
    
    // Can go forward only if current tab is valid
    if (activeTab === 'skills' && answers.skills.length === 0) return false;
    if (activeTab === 'interests' && answers.interests.length === 0) return false;
    if (activeTab === 'problems' && !answers.problemType) return false;
    
    return true;
  };

  const handleTabChange = (tabId: FormTab) => {
    if (canNavigateToTab(tabId)) {
      setActiveTab(tabId);
      setError(null);
    }
  };

  const handleNextQuestion = () => {
    const tabIndex = tabs.findIndex(tab => tab.id === activeTab);
    if (tabIndex < tabs.length - 1) {
      const nextTab = tabs[tabIndex + 1];
      handleTabChange(nextTab.id);
    }
  };

  const canShowNextButton = (): boolean => {
    const tabIndex = tabs.findIndex(tab => tab.id === activeTab);
    const isLastTab = tabIndex === tabs.length - 1;
    
    if (isLastTab) return false; // Don't show on last question
    
    // Show Next button when current tab has selections
    if (activeTab === 'skills' && answers.skills.length > 0) return true;
    if (activeTab === 'interests' && answers.interests.length > 0) return true;
    
    return false;
  };

  const handleGenerateIdeas = async () => {
    if (!isFormValid()) {
      const errorMsg = t('aiIdeaGenerator.completeAllFields');
      setError(errorMsg);
      onError?.(errorMsg);
      return;
    }

    setGenerating(true);
    setError(null);
    
    try {
      console.log('🌐 Generating ideas with system language:', { language: uiLanguage, targetMarket, detailLevel });
      const result = await AIIdeaGeneratorService.generateIdeas(answers, uiLanguage, targetMarket, detailLevel);
      
      // Deduct points after successful generation
      console.log('💰 Checking if user is authenticated for points deduction:', { userId: user?.id, hasUser: !!user });
      if (user?.id) {
        console.log('💰 User authenticated, proceeding with points deduction');
        const { AIPointsService } = await import('../../services/aiPointsService');
        const journeyId = new URLSearchParams(window.location.search).get('journeyId') || 'unknown';
        console.log('💰 Journey ID for transaction:', journeyId);
        
        const deductResult = await AIPointsService.deductPoints(
          user.id,
          'ideaGeneration',
          journeyId
        );
        
        console.log('💰 Points deduction result:', deductResult);
        
        if (!deductResult.success) {
          console.error('❌ Failed to deduct points:', deductResult.error);
          // Don't fail the operation, just log the error
        } else {
          console.log('✅ Successfully deducted points for AI idea generation');
        }
      } else {
        console.warn('⚠️ User not authenticated, skipping points deduction');
      }
      
      onIdeasGenerated(result.ideas);
      setError(null);
    } catch (err) {
      let errorMessage = t('aiIdeaGenerator.errorGenerating');
      
      if (err && typeof err === 'object' && 'code' in err) {
        const aiError = err as AIServiceError;
        switch (aiError.code) {
          case 'INVALID_INPUT':
            errorMessage = t('aiIdeaGenerator.errorInvalidInput');
            break;
          case 'RATE_LIMITED':
            errorMessage = t('aiIdeaGenerator.errorRateLimited');
            break;
          case 'API_UNAVAILABLE':
          case 'NETWORK_ERROR':
            errorMessage = t('aiIdeaGenerator.errorApiUnavailable');
            break;
          default:
            errorMessage = aiError.message || errorMessage;
        }
      }
      
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const handleSkillsChange = (skills: string[]) => {
    setAnswers(prev => ({ ...prev, skills }));
    if (error) setError(null);
  };

  const handleInterestsChange = (interests: string[]) => {
    setAnswers(prev => ({ ...prev, interests }));
    if (error) setError(null);
  };

  const handleProblemTypeChange = (problemType: string | string[]) => {
    // Since we're using single-select mode, extract the first item if array is passed
    const singleProblemType = Array.isArray(problemType) ? problemType[0] || '' : problemType;
    setAnswers(prev => ({ ...prev, problemType: singleProblemType }));
    if (error) setError(null);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'skills':
        return (
          <SkillSelector
            value={answers.skills}
            onChange={handleSkillsChange}
            error={error && answers.skills.length === 0 ? t('aiIdeaGenerator.selectAtLeastOneSkill') : undefined}
            showNextButton={canShowNextButton()}
            onNextQuestion={handleNextQuestion}
          />
        );
      case 'interests':
        return (
          <IndustrySelector
            value={answers.interests}
            onChange={handleInterestsChange}
            error={error && answers.interests.length === 0 ? t('aiIdeaGenerator.selectAtLeastOneInterest') : undefined}
            showNextButton={canShowNextButton()}
            onNextQuestion={handleNextQuestion}
          />
        );
      case 'problems':
        return (
          <ProblemTypeSelector
            value={answers.problemType}
            onChange={handleProblemTypeChange}
            error={error && !answers.problemType ? t('aiIdeaGenerator.selectProblemType') : undefined}
            multiSelect={false} // Keep single select for this form
            showRecommended={true}
            userSkills={answers.skills}
            userInterests={answers.interests}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <LongRunningLoadingState 
        operation="idea-generation"
        isLoading={generating}
      />
      
      <div className="quickfire-idea-generator">
      {/* Tab Navigation */}
      <div className="form-tabs">
        {tabs.map((tab, index) => {
          const status = getTabStatus(tab.id);
          const isClickable = canNavigateToTab(tab.id);
          
          return (
            <button
              key={tab.id}
              className={`form-tab ${status} ${activeTab === tab.id ? 'active' : ''} ${!isClickable ? 'disabled' : ''}`}
              onClick={() => handleTabChange(tab.id)}
              disabled={!isClickable}
            >
              <div className="tab-header">
                <span className="tab-icon">{tab.icon}</span>
                <span className="tab-title">{tab.title}</span>
                {status === 'completed' && <span className="tab-checkmark">✓</span>}
              </div>
              <span className="tab-subtitle">{tab.subtitle}</span>
              {index < tabs.length - 1 && <div className="tab-connector"></div>}
            </button>
          );
        })}
      </div>
      
      {/* Tab Content */}
      <div className="tab-content">
        {renderTabContent()}
      </div>
      
      {/* Generation Options - Show when all questions are answered */}
      {isFormValid() && (
        <div className="generation-options">
          <MarketSelector
            selectedMarket={targetMarket}
            onMarketChange={setTargetMarket}
            label={t('aiIdeaGenerator.targetMarket')}
            disabled={generating}
            defaultCountry={targetMarket}
          />
          
          <DetailLevelSelector
            selectedLevel={detailLevel}
            onLevelChange={setDetailLevel}
            label={t('aiIdeaGenerator.outputDetailLevel')}
            disabled={generating}
          />
        </div>
      )}
      
      {/* Generate Ideas Button - with top margin when on last question */}
      <div className={`generator-actions ${activeTab === 'problems' ? 'final-step' : ''}`}>
        <button
          onClick={handleGenerateIdeas}
          disabled={generating || !isFormValid()}
          className={`btn btn-primary ${generating ? 'loading' : ''}`}
          aria-label={generating ? t('aiIdeaGenerator.generatingIdeas') : t('aiIdeaGenerator.generateIdeas')}
        >
          {generating ? (
            <span className="button-loading-content">
              <span className="button-loading-spinner button-loading-spinner--inverse" aria-hidden="true"></span>
              {t('aiIdeaGenerator.generating')}
            </span>
          ) : (
            t('aiIdeaGenerator.generateIdeas')
          )}
        </button>
      </div>
      
      {error && (
        <div className="error-message" role="alert" aria-live="polite">
          <span className="error-icon" aria-hidden="true">⚠️</span>
          {error}
        </div>
      )}
      
      <div className="generator-info">
        <p className="info-text">
          {t('aiIdeaGenerator.generatorInfo')}
        </p>
      </div>
      </div>
    </>
  );
};
